/**
 * PRD.305 §9 — Consultation / Visit Note → Labs.
 *
 * Encounter-centric: it shows the laboratory work raised in THIS consultation,
 * and ordering from here stamps the encounter so the order stays anchored to
 * the visit while still landing in the one canonical Laboratory queue.
 *
 * This is a normal configurable Visit Note sub-module, not a hardcode — the
 * `labs` key flows through `DEFAULT_TABS`, the role presets, the guided-note
 * presets and the clinic's `consult_show_labs` gate exactly like every other
 * section. This component only renders the section body.
 */
import { useMemo } from 'react';
import { usePermissions } from '@helios/auth-firebase-web';
import { qLabOrdersForEncounter, qPatientById, type LabOrderRow } from '@helios/data-access';
import { usePowerSyncQuery } from '../../../adapters/powersync';
import { useLaboratoryCapabilities } from '../../../hooks/useLaboratory';
import { useInvoiceModuleEnabled } from '../../../hooks/useClinicMainModules';
import { PERM } from '../../../auth/permissionMap';
import { LabsSurface } from '../../laboratory/LabsSurface';

interface PatientRow {
  id: string;
  full_name: string | null;
  clinic_id?: string | null;
}

interface Props {
  patientId: string;
  encounterId: string;
  visitId: string | null;
  /** Ordering provider for the consultation, when known. */
  providerId?: string | null;
}

export function EncounterLabsTab({ patientId, encounterId, visitId, providerId = null }: Props) {
  const { canAccess } = usePermissions();
  const capabilities = useLaboratoryCapabilities('consultation');
  const invoiceEnabled = useInvoiceModuleEnabled();

  const { data: patients = [] } = usePowerSyncQuery<PatientRow>(qPatientById(patientId));
  const patient = patients[0] ?? null;

  const { data: orders = [], loaded } = usePowerSyncQuery<LabOrderRow>(
    qLabOrdersForEncounter(encounterId),
  );

  const orderContext = useMemo(
    () => ({
      patientId,
      patientName: patient?.full_name ?? 'Patient',
      clinicId: patient?.clinic_id ?? null,
      encounterId,
      visitId,
      orderingProviderId: providerId,
    }),
    [patientId, patient, encounterId, visitId, providerId],
  );

  const canBill = invoiceEnabled && canAccess(PERM.billing);

  return (
    <LabsSurface
      orders={orders}
      loaded={loaded}
      capabilities={capabilities}
      orderContext={orderContext}
      source="consultation"
      encounterId={encounterId}
      canBill={canBill}
    />
  );
}

export default EncounterLabsTab;
